import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { showAlert } from '../../utils/sweetAlertUtils';
import { useDebitOrderMandateApi } from '../../hooks/useDebitOrderMandateApi';
import { useFileApi } from '../../hooks/useFileApi';
import { useFormValidation, ValidationRules } from '../../hooks/useFormValidation';
import FormField from '../../components/common/FormField';
import Button from '../../components/common/Button';
import SignatureCapture from '../../components/common/SignatureCapture';
import { CreateDebitOrderMandateRequestDTO, MandateFormDataDTO, BankOptionDTO } from '../../services/apiClient';
import { base64ToFile } from '../../utils/fileUtils';
import handleChange from '../../utils/formUtils/handleChange';
import AuthLayout from '../../components/layout/AuthLayout';

const DebitOrderMandateForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const invoiceId = searchParams.get('invoiceId');

  const [formData, setFormData] = useState<CreateDebitOrderMandateRequestDTO>({} as CreateDebitOrderMandateRequestDTO);
  const [mandateData, setMandateData] = useState<MandateFormDataDTO | null>(null);
  const [signatureData, setSignatureData] = useState<string | null>(null);

  // Validation rules
  const validationRules: ValidationRules = {
    accountHolder: [{ required: true, message: 'Account holder name is required.' }],
    bankId: [{ required: true, message: 'Please select a bank.' }],
    accountNumber: [
      { required: true, message: 'Account number is required.' },
      { pattern: /^\d+$/, message: 'Account number must contain only digits.' },
    ],
    accountType: [{ required: true, message: 'Please select an account type.' }],
    signedAt: [{ required: true, message: 'Please enter the place where the mandate was signed.' }],
    signatureFileId: [{ required: true, message: 'Signature is required.' }],
  };

  const { errors, validate } = useFormValidation<CreateDebitOrderMandateRequestDTO>(validationRules);

  // API hooks
  const { createMandate, getMandateFormData, isLoading: isLoadingMandate, error: mandateError } = useDebitOrderMandateApi();
  const { uploadFile, isLoading: isUploadingSignature, error: uploadError } = useFileApi();

  const backgroundImage = '/images/NS_Pattern%201_Navy_2025.webp';

  // Fetch mandate form data
  useEffect(() => {
    const fetchMandateFormData = async () => {
      if (!invoiceId) {
        return;
      }

      const response = await getMandateFormData(invoiceId);
      if (response?.data) {
        const data = response.data;
        setMandateData(data);

        const paidUpTo = data.memberPaidUpTo;
        const dayOfMonth = paidUpTo ? new Date(paidUpTo).getDate() : undefined;

        setFormData(prev => ({
          ...prev,
          memberId: data.memberId,
          amount: data.amount,
          ...(dayOfMonth !== undefined && { dayOfMonth }),
          ...(paidUpTo !== undefined && { firstDebitDate: paidUpTo }),
        }));
      } else {
        console.error('Error fetching mandate form data:', mandateError);
      };
    }

    fetchMandateFormData();
  }, [invoiceId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.memberId) {
      await showAlert({
        title: 'Error',
        text: 'Invoice data is missing or invalid. Please contact support.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
      return;
    }

    // Guard: signature must be drawn before anything is uploaded
    if (!signatureData) {
      validate({ ...formData });
      return;
    }

    const filename = `signature-${formData.memberId}-${Date.now()}.webp`;
    const file = base64ToFile(signatureData, filename);

    const uploadResponse = await uploadFile(
      'mandate-signatures',
      filename,
      file
    );

    if (!uploadResponse?.success || !uploadResponse.data) {
      await showAlert({
        title: 'Error',
        text: uploadError || 'Failed to upload signature. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
      return;
    }

    const signatureFileId = uploadResponse.data;
    const dataToValidate = { ...formData, signatureFileId };

    // Validate form data (including signature)
    if (!validate(dataToValidate)) {
      return;
    }

    const requestData: CreateDebitOrderMandateRequestDTO = {
      ...formData,
      signatureFileId,
    };

    const response = await createMandate(requestData);

    if (response?.data) {
      await showAlert({
        title: 'Success',
        text: 'Your debit order mandate has been submitted for processing and an admin will be in touch.',
        icon: 'success',
        confirmButtonText: 'OK',
      });
      navigate('/login');
    }
  };

  // Bank options for dropdown
  const bankOptions = mandateData?.banks?.map((bank) => ({
    value: bank.id,
    label: bank.name || '',
  })) || [];

  // Account type options
  const accountTypeOptions = [
    { value: 'current', label: 'Current' },
    { value: 'savings', label: 'Savings' },
    { value: 'transmission', label: 'Transmission' },
  ];

  if (isLoadingMandate) {
    return (
      <AuthLayout
        title="Debit Order Mandate"
        description="Please complete the form to set up your debit order."
      >
        <div
          className="absolute inset-0 w-full min-h-full bg-cover bg-center transition-opacity duration-500 -z-10"
          style={{ backgroundImage: `url(${backgroundImage})` }}
        />
        <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl text-center z-10">
          <img
            src="/images/logo-linnear-navy.png"
            alt="Natshoot Logo"
            className="mx-auto mb-6 w-56"
          />
          <div className="flex justify-center mb-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
          <p className="text-lg text-gray-600">Loading form...</p>
        </div>
      </AuthLayout>
    );
  }

  if (mandateError || !mandateData) {
    return (
      <AuthLayout
        title="Debit Order Mandate"
        description="Please complete the form to set up your debit order."
      >
        <div
          className="absolute inset-0 w-full min-h-full bg-cover bg-center transition-opacity duration-500 -z-10"
          style={{ backgroundImage: `url(${backgroundImage})` }}
        />
        <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl text-center z-10">
          <img
            src="/images/logo-linnear-navy.png"
            alt="Natshoot Logo"
            className="mx-auto mb-6 w-56"
          />
          <div className="text-red-600 mb-4">
            <p className="text-xl font-semibold">Error</p>
            <p className="text-sm mt-2">{mandateError || 'Unable to load form'}</p>
          </div>
          <button
            onClick={() => navigate('/login')}
            className="mt-4 px-6 py-2 bg-primary text-white rounded hover:bg-primary-dark"
          >
            Go to Login
          </button>
        </div>
      </AuthLayout>
    );
  }

  const formattedAmount = new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 2,
  }).format(mandateData.amount || 0);

  return (
    <AuthLayout
      title="Debit Order Mandate"
      description="Please complete the form to set up your debit order."
    >
      <div className="mt-8">
        {/* Invoice Summary */}
        <div className="bg-gray-100 rounded-lg p-4 mb-6">
          <h2 className="text-sm font-semibold text-gray-700 mb-2">Payment Details</h2>
          <div className="flex justify-between">
            <span className="text-gray-600">Amount:</span>
            <span className="font-semibold text-gray-800">{formattedAmount}</span>
          </div>
          {mandateData.invoiceNumber && (
            <div className="flex justify-between mt-1">
              <span className="text-gray-600">Invoice Number:</span>
              <span className="text-gray-800">{mandateData.invoiceNumber}</span>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Account Holder */}
          <FormField
            id="accountHolder"
            label="Account Holder Name"
            type="text"
            value={formData.accountHolder}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter account holder name"
            error={errors.accountHolder}
            required
            disabled={isLoadingMandate}
          />

          {/* Bank */}
          <FormField
            id="bankId"
            label="Bank"
            type="dropdown"
            value={formData.bankId}
            onChange={(e) => handleChange(e, setFormData)}
            options={bankOptions}
            placeholder="Select your bank"
            error={errors.bankId}
            required
            disabled={isLoadingMandate}
          />

          {/* Account Number */}
          <FormField
            id="accountNumber"
            label="Account Number"
            type="text"
            value={formData.accountNumber}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter account number"
            error={errors.accountNumber}
            required
            disabled={isLoadingMandate}
          />

          {/* Account Type */}
          <FormField
            id="accountType"
            label="Account Type"
            type="dropdown"
            value={formData.accountType}
            onChange={(e) => handleChange(e, setFormData)}
            options={accountTypeOptions}
            placeholder="Select your account type"
            error={errors.accountType}
            required
            disabled={isLoadingMandate}
          />

          {/* Signed At */}
          <FormField
            id="signedAt"
            label="Signed At"
            type="text"
            value={formData.signedAt}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter place where mandate was signed"
            error={errors.signedAt}
            required
            disabled={isLoadingMandate}
            helper="The place where the mandate was signed (e.g. city or town)"
          />

          {/* Signature Capture */}
          <SignatureCapture
            onSave={(data) => setSignatureData(data)}
            onClear={() => setSignatureData(null)}
            disabled={isLoadingMandate}
            error={errors.signatureFileId}
          />

          {/* Submit Button */}
          <div className="flex gap-4">
            <Button
              type="button"
              onClick={() => navigate('/login')}
              className="flex-1 mb-4"
              disabled={isLoadingMandate}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 mb-4 bg-primary hover:bg-primary-dark"
              disabled={isLoadingMandate}
            >
              {isUploadingSignature ? 'Uploading signature...' : isLoadingMandate ? 'Submitting...' : 'Submit Mandate'}
            </Button>
          </div>
        </form>
      </div>
    </AuthLayout>
  );
};

export default DebitOrderMandateForm;
